/**
 * VideoTile — video element wrapper with loading state and overlay.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { shortenAddress } from '@tokenstreaming/shared';

interface Props {
  stream: MediaStream | null;
  muted?: boolean;
  label?: string;
  address?: string;
  isLocal?: boolean;
  connectionQuality?: 'good' | 'fair' | 'poor' | 'unknown';
}

export function VideoTile({
  stream,
  muted = false,
  label,
  address,
  isLocal = false,
  connectionQuality = 'unknown',
}: Props): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  const qualityColor: Record<string, string> = {
    good: 'var(--color-success)',
    fair: 'var(--color-warning)',
    poor: 'var(--color-error)',
    unknown: 'var(--color-text-muted)',
  };

  return (
    <div className={`video-tile ${isLocal ? 'video-tile-local' : 'video-tile-remote'}`}>
      {stream ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={muted || isLocal}
          className="video-element"
        />
      ) : (
        <div className="video-placeholder">
          <div className="video-placeholder-icon">
            {isLocal ? 'You' : 'Peer'}
          </div>
        </div>
      )}

      {/* Overlay */}
      <div className="video-overlay">
        {label && <span className="video-label">{label}</span>}
        {address && <span className="video-address">{shortenAddress(address)}</span>}
        <span
          className="video-quality-dot"
          style={{ backgroundColor: qualityColor[connectionQuality] }}
        />
      </div>
    </div>
  );
}
